/**
 * useClerkWorkspace — the clerk's REAL access context, derived from the
 * capabilities engine (GET /permissions/workspace → getClerkWorkspaces, which
 * derives permission codes from BarristerClerk.capabilities).
 *
 * Replaces the old mock `scopesStore` (hardcoded "Margaret Hughes", localStorage).
 * Everything the clerk sees — nav menu filtering, managed-barrister lists,
 * per-barrister scopes — now flows from the same source as enforcement, so the
 * menu can never disagree with what the clerk can actually do.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchClerkWorkspaceContext } from './delegationApi';

export type Scope = 'Diary' | 'Bookings' | 'Matters' | 'Fees' | 'Documents' | 'Communications' | 'Tasks';

export const ALL_SCOPES: Scope[] = ['Diary', 'Bookings', 'Matters', 'Fees', 'Documents', 'Communications', 'Tasks'];

/** A barrister this clerk supports, with the scopes the barrister has granted. */
export interface ManagedBarrister {
  name: string;
  /** Barrister's User UUID — preferred over name strings for identity checks. */
  userId?: string;
  scopes: Scope[];
}

// Legacy permission codes (derived from capabilities server-side) → nav scopes.
// Mirrors the nav `scope` vocabulary in Sidebar.tsx.
const CODE_TO_SCOPE: Record<string, Scope> = {
  VIEW_MATTERS: 'Matters',
  VIEW_HEARINGS: 'Matters',
  MANAGE_TASKS: 'Tasks',
  RESPOND_TO_ALLOCATIONS: 'Bookings',
  VIEW_CALENDAR: 'Diary',
  COORDINATE_SCHEDULING: 'Diary',
  MANAGE_DOCUMENTS: 'Documents',
  VIEW_COMMUNICATIONS: 'Communications',
  SEND_COMMUNICATIONS: 'Communications',
  MANAGE_FEES: 'Fees',
};

export function scopesFromPermissionCodes(codes: string[]): Scope[] {
  const out = new Set<Scope>();
  for (const c of codes) {
    const s = CODE_TO_SCOPE[c];
    if (s) out.add(s);
  }
  return [...out];
}

/**
 * @param enabled pass `isClerk` — the endpoint is clerk-only (403 otherwise),
 *   so shared pages (Sidebar, BarristerDetailPage) must gate the fetch.
 */
export function useClerkWorkspace(enabled = true) {
  const { data = [], isSuccess } = useQuery({
    queryKey: ['clerk-workspace'],
    queryFn: fetchClerkWorkspaceContext,
    enabled,
    staleTime: 60_000,
  });

  const managedBarristers: ManagedBarrister[] = data.map((e) => ({
    name: e.barristerName,
    userId: e.barristerUserId,
    scopes: scopesFromPermissionCodes(e.permissions ?? []),
  }));

  const scopes = [...new Set(managedBarristers.flatMap((m) => m.scopes))];

  return { entries: data, managedBarristers, scopes, isLoaded: isSuccess };
}
